/**
 * Disco Cart Banner - Block Cart Support
 *
 * Handles dynamic banner updates for WooCommerce Cart Block.
 * Subscribes to cart store changes and refreshes banner via AJAX.
 */
(function () {
    'use strict';

    const SELECTORS = {
        cartBlock: '.wp-block-woocommerce-cart',
        bannerWrapper: '.disco-cart-banners-wrapper',
    };

    const DEBOUNCE_DELAY_MS = 500;
    const WC_CART_STORE = 'wc/store/cart';
    const AJAX_ACTION = 'disco_refresh_cart_banner';

    const config = window.discoCartBanner;

    if (!config) {
        return;
    }

    let isRefreshing = false;
    let debounceTimer = null;
    let previousCartItemsJson = null;

    /**
     * Find or create the banner wrapper element.
     *
     * @returns {HTMLElement|null} The wrapper element or null if cart block not found.
     */
    function getOrCreateBannerWrapper() {
        const cartBlock = document.querySelector(SELECTORS.cartBlock);

        if (!cartBlock) {
            return null;
        }

        let wrapper = document.querySelector(SELECTORS.bannerWrapper);

        if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = 'disco-cart-banners-wrapper';
            cartBlock.parentNode.insertBefore(wrapper, cartBlock);
        }

        return wrapper;
    }

    /**
     * Insert or update banner HTML in the DOM.
     *
     * @param {string} html - The banner HTML content.
     */
    function renderBanner(html) {
        const wrapper = getOrCreateBannerWrapper();

        if (wrapper) {
            wrapper.innerHTML = html || '';
        }
    }

    /**
     * Build the AJAX request body.
     *
     * @returns {string} URL-encoded request body.
     */
    function buildRequestBody() {
        const params = new URLSearchParams({
            action: AJAX_ACTION,
            nonce: config.nonce,
        });

        return params.toString();
    }

    /**
     * Handle successful AJAX response.
     *
     * @param {string} responseText - Raw response text from server.
     */
    function handleRefreshResponse(responseText) {
        try {
            const response = JSON.parse(responseText);

            if (response.success && response.data?.html !== undefined) {
                renderBanner(response.data.html);
            }
        } catch (error) {
            console.error('Disco: Failed to parse banner response', error);
        }
    }

    /**
     * Fetch updated banner HTML from the server.
     */
    function refreshBanner() {
        if (isRefreshing) {
            return;
        }

        isRefreshing = true;

        const xhr = new XMLHttpRequest();
        xhr.open('POST', config.ajaxUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');

        xhr.onreadystatechange = function () {
            const isComplete = xhr.readyState === XMLHttpRequest.DONE;

            if (!isComplete) {
                return;
            }

            isRefreshing = false;

            if (xhr.status === 200) {
                handleRefreshResponse(xhr.responseText);
            }
        };

        xhr.send(buildRequestBody());
    }

    /**
     * Schedule a debounced banner refresh.
     */
    function scheduleRefresh() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(refreshBanner, DEBOUNCE_DELAY_MS);
    }

    /**
     * Check if WooCommerce Blocks data API is available.
     *
     * @returns {boolean} True if the API is available.
     */
    function isWcBlocksApiAvailable() {
        return typeof wp !== 'undefined' && wp.data?.subscribe && wp.data?.select;
    }

    /**
     * Handle cart store state changes.
     */
    function handleCartStoreChange() {
        const store = wp.data.select(WC_CART_STORE);

        if (!store) {
            return;
        }

        const cartData = store.getCartData();
        const cartItemsJson = JSON.stringify(cartData.items);
        const hasCartChanged = previousCartItemsJson !== null && previousCartItemsJson !== cartItemsJson;

        if (hasCartChanged) {
            scheduleRefresh();
        }

        previousCartItemsJson = cartItemsJson;
    }

    /**
     * Subscribe to WooCommerce Blocks cart store for real-time updates.
     */
    function subscribeToCartStore() {
        if (!isWcBlocksApiAvailable()) {
            return;
        }

        wp.data.subscribe(handleCartStoreChange);
    }

    /**
     * Initialize the cart banner functionality.
     */
    function init() {
        renderBanner(config.initialHtml);
        subscribeToCartStore();
    }

    document.addEventListener('DOMContentLoaded', init);
})();
